import { decodeJwt } from './jwt';
import { generatePkce, randomState } from './pkce';
import { keysWithPrefix, resolveStorage } from './storage';
import {
  OidcError,
  type CallbackResult,
  type IdTokenClaims,
  type LoginOptions,
  type OidcClient,
  type OidcConfig,
  type OidcDiscoveryDocument,
  type OidcListener,
  type TokenSet,
} from './types';

export const TX_STORAGE_PREFIX = 'zagru.oidc.tx.';
const TX_MAX_AGE_MS = 15 * 60 * 1000;
/** Message type posted by `/auth/silent-callback.html` to its parent window. */
export const SILENT_CALLBACK_MESSAGE = 'zagru.oidc.silent-callback';

interface LoginTransaction {
  state: string;
  nonce: string;
  verifier: string;
  /** The redirect_uri used by the authorization request; the code exchange must repeat it. */
  redirectUri?: string;
  returnTo?: string;
  createdAt: number;
}

/**
 * Callbacks in flight, keyed by `state`. React StrictMode mounts effects twice and a user can double-submit; the
 * login transaction is single-use, so a second exchange for the same state must join the first instead of failing
 * with "unknown or expired login transaction".
 */
const inflightCallbacks = new Map<string, Promise<CallbackResult>>();

interface TokenResponse {
  access_token: string;
  id_token?: string;
  refresh_token?: string;
  expires_in?: number;
  scope?: string;
  token_type?: string;
}

export interface CreateOidcClientOptions extends OidcConfig {
  /** Replaces `location.assign` (tests). */
  navigate?: (url: string) => void;
  /** Clock source in epoch milliseconds (tests). */
  now?: () => number;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}

function trimSlash(s: string): string {
  return s.replace(/\/+$/, '');
}

/**
 * In-house OIDC Authorization Code + PKCE client (no third-party auth library).
 *
 * Security notes:
 * - Tokens live in memory only; the login transaction (state, nonce, code verifier) is kept in
 *   sessionStorage under `zagru.oidc.tx.<state>` for the duration of the redirect.
 * - The id_token is decoded and its `nonce`, `iss`, `aud`, `exp` claims are checked, but the
 *   signature is NOT verified client-side: the API (resource server) validates every access token
 *   against the issuer's JWKS (ADR-0005). Claims are used for UI state only.
 */
export function createOidcClient(config: CreateOidcClientOptions): OidcClient {
  const storage = resolveStorage(config.storage);
  const fetchImpl: typeof fetch = config.fetch ?? ((...args) => globalThis.fetch(...args));
  const now = config.now ?? (() => Date.now());
  const navigate = config.navigate ?? ((url: string) => window.location.assign(url));
  const skewMs = (config.clockSkewSeconds ?? 60) * 1000;
  const leewayMs = (config.refreshLeewaySeconds ?? 60) * 1000;
  const scopes = config.scopes && config.scopes.length > 0 ? config.scopes : ['openid', 'profile', 'email'];

  let discovery: Promise<OidcDiscoveryDocument> | null = null;
  let tokens: TokenSet | null = null;
  let claims: IdTokenClaims | null = null;
  let refreshTimer: ReturnType<typeof setTimeout> | null = null;
  let inflightRefresh: Promise<TokenSet | null> | null = null;
  let disposed = false;
  const listeners = new Set<OidcListener>();

  function notify(): void {
    for (const l of listeners) l(tokens, claims);
  }

  function clearTimer(): void {
    if (refreshTimer !== null) {
      clearTimeout(refreshTimer);
      refreshTimer = null;
    }
  }

  function scheduleRefresh(): void {
    clearTimer();
    if (disposed || !tokens || (!tokens.refreshToken && !config.silentRedirectUri)) return;
    const delay = Math.max(tokens.expiresAt - leewayMs - now(), 1000);
    refreshTimer = setTimeout(() => {
      refreshTimer = null;
      void refresh();
    }, delay);
  }

  function setSession(next: TokenSet | null, nextClaims: IdTokenClaims | null): void {
    tokens = next;
    claims = nextClaims;
    scheduleRefresh();
    notify();
  }

  async function discover(): Promise<OidcDiscoveryDocument> {
    if (!discovery) {
      discovery = (async () => {
        if (!config.issuer) throw new OidcError('client.missing_issuer', 'OIDC issuer is not configured');
        const url = `${trimSlash(config.issuer)}/.well-known/openid-configuration`;
        let res: Response;
        try {
          res = await fetchImpl(url, { headers: { Accept: 'application/json' } });
        } catch (err) {
          throw new OidcError('client.discovery_failed', err instanceof Error ? err.message : String(err));
        }
        if (!res.ok) throw new OidcError('client.discovery_failed', `HTTP ${res.status} from ${url}`);
        const doc = (await res.json()) as unknown;
        if (!isRecord(doc) || typeof doc.authorization_endpoint !== 'string' || typeof doc.token_endpoint !== 'string') {
          throw new OidcError('client.discovery_failed', 'Discovery document is missing required endpoints');
        }
        return doc as OidcDiscoveryDocument;
      })().catch((err: unknown) => {
        discovery = null;
        throw err;
      });
    }
    return discovery;
  }

  function txKey(state: string): string {
    return `${TX_STORAGE_PREFIX}${state}`;
  }

  function pruneTransactions(): void {
    for (const key of keysWithPrefix(storage, TX_STORAGE_PREFIX)) {
      try {
        const tx = JSON.parse(storage.getItem(key) ?? '') as Partial<LoginTransaction>;
        if (typeof tx.createdAt !== 'number' || now() - tx.createdAt > TX_MAX_AGE_MS) storage.removeItem(key);
      } catch {
        storage.removeItem(key);
      }
    }
  }

  /** Stores a fresh login transaction and returns the authorization URL that starts it. */
  async function authorizationUrl(redirectUri: string, options: LoginOptions): Promise<{ url: string; state: string }> {
    const doc = await discover();
    const pkce = await generatePkce();
    const tx: LoginTransaction = { state: randomState(), nonce: randomState(), verifier: pkce.verifier, redirectUri, returnTo: options.returnTo, createdAt: now() };
    pruneTransactions();
    storage.setItem(txKey(tx.state), JSON.stringify(tx));
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: config.clientId,
      redirect_uri: redirectUri,
      scope: scopes.join(' '),
      state: tx.state,
      nonce: tx.nonce,
      code_challenge: pkce.challenge,
      code_challenge_method: 'S256',
    });
    if (config.audience) params.set('audience', config.audience);
    if (options.prompt) params.set('prompt', options.prompt);
    if (options.loginHint) params.set('login_hint', options.loginHint);
    if (options.uiLocales) params.set('ui_locales', options.uiLocales);
    if (options.tenantHint) params.set('zagru_tenant', options.tenantHint);
    const sep = doc.authorization_endpoint.includes('?') ? '&' : '?';
    return { url: `${doc.authorization_endpoint}${sep}${params.toString()}`, state: tx.state };
  }

  async function login(options: LoginOptions = {}): Promise<void> {
    const { url } = await authorizationUrl(config.redirectUri, options);
    navigate(url);
  }

  async function loginWithPassword(username: string, password: string): Promise<TokenSet> {
    const doc = await discover();
    const response = await postToken(doc, {
      grant_type: 'password',
      username,
      password,
      scope: scopes.join(' '),
    });
    const set = toTokenSet(response);
    const nextClaims = claimsFrom(set, doc, undefined);
    setSession(set, nextClaims);
    return set;
  }

  async function postToken(doc: OidcDiscoveryDocument, form: Record<string, string>): Promise<TokenResponse> {
    const body = new URLSearchParams({ client_id: config.clientId, ...form });
    let res: Response;
    try {
      res = await fetchImpl(doc.token_endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', Accept: 'application/json' },
        body: body.toString(),
      });
    } catch (err) {
      throw new OidcError('client.token_request_failed', err instanceof Error ? err.message : String(err));
    }
    let json: unknown = null;
    try {
      json = await res.json();
    } catch {
      json = null;
    }
    if (!res.ok) {
      const error = isRecord(json) && typeof json.error === 'string' ? json.error : `http_${res.status}`;
      const description = isRecord(json) && typeof json.error_description === 'string' ? json.error_description : undefined;
      throw new OidcError(error, description);
    }
    if (!isRecord(json) || typeof json.access_token !== 'string') {
      throw new OidcError('client.invalid_token_response', 'Token response has no access_token');
    }
    return json as unknown as TokenResponse;
  }

  function toTokenSet(res: TokenResponse): TokenSet {
    let expiresAt = now() + (typeof res.expires_in === 'number' ? res.expires_in * 1000 : 300_000);
    if (typeof res.expires_in !== 'number') {
      try {
        const exp = decodeJwt(res.access_token).payload.exp;
        if (typeof exp === 'number') expiresAt = exp * 1000;
      } catch {
        // opaque access token: keep the default lifetime
      }
    }
    const set: TokenSet = { accessToken: res.access_token, expiresAt, tokenType: res.token_type ?? 'Bearer' };
    if (res.id_token) set.idToken = res.id_token;
    if (res.refresh_token) set.refreshToken = res.refresh_token;
    if (res.scope) set.scope = res.scope;
    return set;
  }

  function validateIdToken(idToken: string, expectedNonce: string | undefined, doc: OidcDiscoveryDocument): IdTokenClaims {
    let payload: Record<string, unknown>;
    try {
      payload = decodeJwt(idToken).payload;
    } catch (err) {
      throw new OidcError('client.invalid_id_token', err instanceof Error ? err.message : 'undecodable id_token');
    }
    if (typeof payload.sub !== 'string' || !payload.sub) throw new OidcError('client.invalid_id_token', 'missing sub');
    if (expectedNonce !== undefined && payload.nonce !== expectedNonce) throw new OidcError('client.invalid_id_token', 'nonce mismatch');
    const expectedIssuer = trimSlash(doc.issuer || config.issuer);
    if (typeof payload.iss !== 'string' || trimSlash(payload.iss) !== expectedIssuer) {
      throw new OidcError('client.invalid_id_token', 'issuer mismatch');
    }
    const aud = payload.aud;
    const audiences = Array.isArray(aud) ? aud : typeof aud === 'string' ? [aud] : [];
    if (!audiences.includes(config.clientId)) throw new OidcError('client.invalid_id_token', 'audience mismatch');
    if (Array.isArray(aud) && aud.length > 1 && payload.azp !== undefined && payload.azp !== config.clientId) {
      throw new OidcError('client.invalid_id_token', 'azp mismatch');
    }
    if (typeof payload.exp !== 'number' || payload.exp * 1000 + skewMs < now()) throw new OidcError('client.invalid_id_token', 'expired');
    if (typeof payload.iat === 'number' && payload.iat * 1000 - skewMs > now()) throw new OidcError('client.invalid_id_token', 'issued in the future');
    return payload as IdTokenClaims;
  }

  function claimsFrom(set: TokenSet, doc: OidcDiscoveryDocument, nonce: string | undefined): IdTokenClaims | null {
    if (set.idToken) return validateIdToken(set.idToken, nonce, doc);
    try {
      const payload = decodeJwt(set.accessToken).payload;
      return typeof payload.sub === 'string' ? (payload as IdTokenClaims) : null;
    } catch {
      return null;
    }
  }

  function readTransaction(state: string | null): LoginTransaction | null {
    if (!state) return null;
    const raw = storage.getItem(txKey(state));
    if (!raw) return null;
    try {
      return JSON.parse(raw) as LoginTransaction;
    } catch {
      return null;
    }
  }

  async function exchange(params: URLSearchParams): Promise<CallbackResult> {
    const state = params.get('state');
    const error = params.get('error');
    if (error) {
      // The transaction is finished either way; keep its returnTo so an interaction-required answer to a silent
      // top-level attempt can still send the user back to where they were going after they sign in.
      const tx = readTransaction(state);
      if (state) storage.removeItem(txKey(state));
      throw new OidcError(error, params.get('error_description') ?? undefined, tx?.returnTo);
    }
    const code = params.get('code');
    if (!code || !state) throw new OidcError('client.invalid_callback', 'Missing code or state');
    const raw = storage.getItem(txKey(state));
    if (!raw) throw new OidcError('client.invalid_state', 'Unknown or expired login transaction');
    storage.removeItem(txKey(state));
    let tx: LoginTransaction;
    try {
      tx = JSON.parse(raw) as LoginTransaction;
    } catch {
      throw new OidcError('client.invalid_state', 'Corrupt login transaction');
    }
    if (tx.state !== state) throw new OidcError('client.invalid_state', 'State mismatch');
    const doc = await discover();
    const response = await postToken(doc, {
      grant_type: 'authorization_code',
      code,
      redirect_uri: tx.redirectUri ?? config.redirectUri,
      code_verifier: tx.verifier,
    });
    const set = toTokenSet(response);
    const nextClaims = claimsFrom(set, doc, tx.nonce);
    setSession(set, nextClaims);
    const result: CallbackResult = { tokens: set };
    if (tx.returnTo) result.returnTo = tx.returnTo;
    return result;
  }

  async function handleCallback(url?: string): Promise<CallbackResult> {
    const target = new URL(url ?? window.location.href, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const params = target.searchParams;
    const key = params.get('state') ?? '';
    const pending = key ? inflightCallbacks.get(key) : undefined;
    if (pending) return pending;
    const run = exchange(params);
    if (key) {
      inflightCallbacks.set(key, run);
      // Keep the settled promise briefly so a StrictMode re-mount that arrives just after completion still joins it.
      const forget = () => setTimeout(() => inflightCallbacks.delete(key), 10_000);
      run.then(forget, forget);
    }
    return run;
  }

  let inflightSilent: Promise<TokenSet> | null = null;

  /** Hidden-iframe `prompt=none` authorization, answered by `/auth/silent-callback.html` via postMessage. */
  function signInSilently(): Promise<TokenSet> {
    if (inflightSilent) return inflightSilent;
    const silentUri = config.silentRedirectUri;
    if (!silentUri || typeof window === 'undefined' || typeof document === 'undefined') {
      return Promise.reject(new OidcError('client.silent_unavailable', 'Silent sign-in is not configured'));
    }
    const timeoutMs = config.silentTimeoutMs ?? 6000;
    inflightSilent = (async () => {
      const { url, state } = await authorizationUrl(silentUri, { prompt: 'none' });
      const expectedOrigin = new URL(silentUri, window.location.origin).origin;
      const frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.setAttribute('tabindex', '-1');
      frame.title = 'Session check';
      frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      try {
        const callbackUrl = await new Promise<string>((resolve, reject) => {
          const timer = setTimeout(() => {
            cleanup();
            reject(new OidcError('client.silent_timeout', 'The identity provider did not answer the session check'));
          }, timeoutMs);
          function onMessage(event: MessageEvent): void {
            if (event.origin !== expectedOrigin || event.source !== frame.contentWindow) return;
            const data = event.data as { type?: unknown; url?: unknown } | null;
            if (!data || data.type !== SILENT_CALLBACK_MESSAGE || typeof data.url !== 'string') return;
            const answered = new URL(data.url).searchParams.get('state');
            if (answered !== state) return;
            cleanup();
            resolve(data.url);
          }
          function cleanup(): void {
            clearTimeout(timer);
            window.removeEventListener('message', onMessage);
          }
          window.addEventListener('message', onMessage);
          frame.src = url;
          document.body.appendChild(frame);
        });
        const result = await handleCallback(callbackUrl);
        return result.tokens;
      } finally {
        storage.removeItem(txKey(state));
        frame.remove();
      }
    })().finally(() => {
      inflightSilent = null;
    });
    return inflightSilent;
  }

  async function silentOrNull(): Promise<TokenSet | null> {
    if (!config.silentRedirectUri) return null;
    try {
      return await signInSilently();
    } catch {
      return null;
    }
  }

  async function refresh(): Promise<TokenSet | null> {
    if (inflightRefresh) return inflightRefresh;
    const current = tokens;
    if (!current) return null;
    inflightRefresh = (async () => {
      try {
        if (current.refreshToken) {
          try {
            const doc = await discover();
            const response = await postToken(doc, { grant_type: 'refresh_token', refresh_token: current.refreshToken });
            const set = toTokenSet(response);
            if (!set.refreshToken) set.refreshToken = current.refreshToken;
            if (!set.idToken && current.idToken) set.idToken = current.idToken;
            const nextClaims = set.idToken && set.idToken !== current.idToken ? claimsFrom(set, doc, undefined) : (claims ?? claimsFrom(set, doc, undefined));
            setSession(set, nextClaims);
            return set;
          } catch {
            // A rotated-away or idle-expired refresh token: the SSO session may still be alive.
          }
        }
        const restored = await silentOrNull();
        if (restored) return restored;
        setSession(null, null);
        return null;
      } finally {
        inflightRefresh = null;
      }
    })();
    return inflightRefresh;
  }

  async function getAccessToken(): Promise<string | null> {
    if (!tokens) return null;
    if (tokens.expiresAt - now() > leewayMs) return tokens.accessToken;
    if (tokens.refreshToken || config.silentRedirectUri) {
      const refreshed = await refresh();
      if (refreshed) return refreshed.accessToken;
    }
    return tokens && tokens.expiresAt > now() ? tokens.accessToken : null;
  }

  let inflightLogout: Promise<boolean> | null = null;

  /**
   * Single-flight: the end-session redirect happens once per document. A second call (StrictMode, a double click)
   * joins the first — otherwise it would start another navigation without the id token hint and replace it.
   */
  function logout(options: { returnTo?: string } = {}): Promise<boolean> {
    if (inflightLogout) return inflightLogout;
    const idToken = tokens?.idToken;
    setSession(null, null);
    for (const key of keysWithPrefix(storage, TX_STORAGE_PREFIX)) storage.removeItem(key);
    inflightLogout = (async () => {
      let doc: OidcDiscoveryDocument | null = null;
      try {
        doc = await discover();
      } catch {
        doc = null;
      }
      if (!doc?.end_session_endpoint) {
        inflightLogout = null;
        return false;
      }
      const params = new URLSearchParams({ client_id: config.clientId });
      if (idToken) params.set('id_token_hint', idToken);
      const postLogout = options.returnTo ?? config.postLogoutRedirectUri;
      if (postLogout) params.set('post_logout_redirect_uri', postLogout);
      const sep = doc.end_session_endpoint.includes('?') ? '&' : '?';
      navigate(`${doc.end_session_endpoint}${sep}${params.toString()}`);
      return true;
    })();
    return inflightLogout;
  }

  return {
    discover,
    login,
    loginWithPassword,
    handleCallback,
    refresh,
    signInSilently,
    clearLocalSession: () => setSession(null, null),
    getAccessToken,
    getTokens: () => tokens,
    getClaims: () => claims,
    logout,
    isAuthenticated: () => tokens !== null && tokens.expiresAt > now(),
    dispose: () => {
      disposed = true;
      clearTimer();
      listeners.clear();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    setTokens: (next) => {
      let nextClaims: IdTokenClaims | null = null;
      if (next) {
        const source = next.idToken ?? next.accessToken;
        try {
          const payload = decodeJwt(source).payload;
          nextClaims = typeof payload.sub === 'string' ? (payload as IdTokenClaims) : null;
        } catch {
          nextClaims = null;
        }
      }
      setSession(next, nextClaims);
    },
  };
}
